import React, { useEffect, useRef, useState } from 'react';
import { FiZap, FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Select from 'react-select';
import services from '../../services';
import labelService from '../../services/labelService';
import AppModal from '../../common/AppModal';
import AppSelect from '../../common/AppSelect';
import ChecklistItemAction, { hasChecklistItemAction } from './ChecklistItemAction';
import { ORDER_OPTIONAL, OrderRuleRadio, OrderBadge, orderModeOf, orderFlagsFromSaved, orderPayload } from './ChecklistOrder';

export const PROPERTY_STATUS_LINK_TYPE = 'propertyStatusLink';

// Extra actions of the move mode ("+ Add action"): run on the ticket after it landed on the new board
const EXTRA_ACTION_OPTIONS = [
    { label: 'assign it to', value: 'assignTo' },
    { label: 'assign with round robin to', value: 'assignToRoundRobin' },
    { label: 'unassign it', value: 'unassign' },
    { label: 'mark it complete', value: 'markComplete' },
    { label: 'mark it incomplete', value: 'markIncomplete' },
    { label: 'Labels', value: 'setEmailLabel' },
    { label: 'Set custom property', value: 'setCustomProperty' },
];
const EXTRA_ACTION_VALUES = EXTRA_ACTION_OPTIONS.map((o) => o.value);
// actions that contradict each other: only one of each group per automation
const EXCLUSIVE_GROUPS = [['assignTo', 'assignToRoundRobin', 'unassign'], ['markComplete', 'markIncomplete']];

const flattenLabels = (labels, parentPath = [], parentId = null) =>
    (labels || []).flatMap((l) => {
        const path = [...parentPath, l.name];
        return [
            { label: path.join('/'), value: JSON.stringify({ parentId, childId: l.id, name: l.name }) },
            ...flattenLabels(l.children, path, l.id),
        ];
    });

// Saved rule action -> form row
const actionToRow = (a) => {
    const row = { action: a.action };
    if (a.action === 'assignTo') row.user = a.entityId || '';
    if (a.action === 'assignToRoundRobin') row.users = asArrayOf(a.entityIds).map((u) => ({ value: u.entityId, label: u.name || u.entityId }));
    if (a.action === 'setEmailLabel') row.label = { name: a.labelName || '', parentId: a.parentId || null };
    if (a.action === 'setCustomProperty') {
        row.propertyId = a.customProperty || '';
        row.fieldIds = asArrayOf(a.customField).map((f) => (f && typeof f === 'object' ? f.id : f)).filter(Boolean);
    }
    return row;
};
const asArrayOf = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const TRIP_STATUS_OPTIONS = [
    { value: 'new_request', label: 'New Request' },
    { value: 'in_negotiation', label: 'In Negotiation' },
    { value: 'booked', label: 'Booked' },
    { value: 'fully_paid', label: 'Fully Paid' },
    { value: 'pre_trip', label: 'Pre Trip' },
    { value: 'post_trip', label: 'Post Trip' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'lost', label: 'Lost' },
];

// THEN: either auto manage the trip status, or move the ticket between lists (board + list only)
const ACTION_KIND_OPTIONS = [
    { value: 'trip', label: 'set the overall trip status' },
    { value: 'list', label: 'move tickets between the lists' },
];

const WHEN_OPTIONS = [
    { value: 'new', label: 'a new mail is received' },
    { value: 'itemAdded', label: 'an item is added / moved to a list' },
    { value: 'noReplyToCustomer', label: 'there is no reply to a customer' },
    { value: 'noReplyFromCustomer', label: 'there is no reply from a customer' },
    { value: PROPERTY_STATUS_LINK_TYPE, label: 'a property status is set on a ticket' },
    { value: 'listProcess', label: 'tickets must follow the lists in order (ticket process)' },
    { value: 'ticketPriority', label: 'tickets are ordered by status priority (ticket priority)' },
];

const selectStyle = { height: '38px', fontSize: '13px', minWidth: '200px' };

// Multi select (statuses / lists) for the board-and-list action; menu is portaled so the modal cannot clip it
const multiSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        minWidth: '260px',
        borderRadius: '8px',
        fontSize: '13px',
        borderColor: state.isFocused ? '#1a7d3e' : '#d1fae5',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(26,125,62,0.12)' : 'none',
        '&:hover': { borderColor: '#1a7d3e' },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 100000 }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        cursor: 'pointer',
        backgroundColor: state.isSelected ? '#e8f5e9' : state.isFocused ? '#f0fdf4' : 'transparent',
        color: state.isSelected ? '#1a7d3e' : '#374151',
    }),
    multiValue: (base) => ({ ...base, backgroundColor: '#e8f5e9', borderRadius: '6px' }),
    multiValueLabel: (base) => ({ ...base, color: '#1a7d3e', fontSize: '12.5px', fontWeight: 600 }),
    multiValueRemove: (base) => ({ ...base, color: '#ef4444', ':hover': { backgroundColor: '#fecaca', color: '#b91c1c' } }),
    indicatorSeparator: () => ({ display: 'none' }),
};
const labelStyle = { fontSize: '12px', fontWeight: 600, color: '#1a7d3e', width: '60px' };

const asIds = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const fieldLabel = (item) => {
    const cf = item?.customField ?? {};
    const getLabel = (v) =>
        typeof v === 'object' && v !== null ? v.label ?? v.name ?? v.value ?? '' : v ?? '';
    const value = cf.value;
    const label = Array.isArray(value) ? value.map(getLabel).join(', ') : getLabel(value);
    return label || cf.name || 'Unnamed';
};

/**
 * Create / edit a "property status -> trip status / board list" link rule.
 * Stored as a Rule of type 'propertyStatusLink'.
 */
const PropertyStatusLinkForm = ({ show, onHide, onCancel, editId, boardName, onTypeChange, onSaved }) => {
    const [boardId, setBoardId] = useState('');
    const [propertyId, setPropertyId] = useState('');
    const [fieldIds, setFieldIds] = useState([]);
    const [actionKind, setActionKind] = useState('trip');
    const [tripStatus, setTripStatus] = useState('');
    const [targetBoardId, setTargetBoardId] = useState('');
    const [targetListIds, setTargetListIds] = useState([]);
    const [reverse, setReverse] = useState(false);
    // Property + statuses of the TARGET board the ticket gets when it is moved (the IF property may not exist there)
    const [targetPropertyId, setTargetPropertyId] = useState('');
    const [targetFieldIds, setTargetFieldIds] = useState([]);
    const [targetPropertyOptions, setTargetPropertyOptions] = useState([]);
    const [targetFieldOptions, setTargetFieldOptions] = useState([]);

    const [propertyOptions, setPropertyOptions] = useState([]);
    const [fieldOptions, setFieldOptions] = useState([]);
    const [listOptions, setListOptions] = useState([]);
    const [saving, setSaving] = useState(false);
    // "+ Add action" rows (move mode) and what their pickers offer: users / properties of the NEW board, labels
    const [extraActions, setExtraActions] = useState([]);
    const [targetUserOptions, setTargetUserOptions] = useState([]);
    const [labelOptions, setLabelOptions] = useState([]);
    const [fieldsByProperty, setFieldsByProperty] = useState({});
    // Checklist (move mode): added to the ticket when this automation moves it, for the list it lands in
    const [showChecklist, setShowChecklist] = useState(false);
    const [checklistItems, setChecklistItems] = useState([]); // [{ id, text, checksBelow, onCheck }]
    const [checklistMandatory, setChecklistMandatory] = useState(false);
    const [checklistOrder, setChecklistOrder] = useState(ORDER_OPTIONAL);
    const newChecklistItem = () => ({ id: `c-${Date.now()}-${Math.random()}`, text: '', checksBelow: false });
    const updateChecklistItem = (id, patch) =>
        setChecklistItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));

    // Users of the target board (assign / round robin)
    useEffect(() => {
        if (!targetBoardId) {
            setTargetUserOptions([]);
            return;
        }
        (async () => {
            try {
                const res = await services.userassignboard.GET_ASSIGN_USER_BY_BOARD_ID(targetBoardId, '', true);
                const users = new Map();
                for (const item of res?.data?.data || []) {
                    const user = item?.User || item;
                    const id = user?.id || item.userId;
                    if (id && !users.has(id)) users.set(id, { value: id, label: user?.name || user?.email || id });
                }
                setTargetUserOptions([...users.values()].sort((a, b) => String(a.label).localeCompare(String(b.label))));
            } catch (e) {
                console.error(e);
                setTargetUserOptions([]);
            }
        })();
    }, [targetBoardId]);

    // Email labels (Labels action)
    useEffect(() => {
        if (!show) return;
        (async () => {
            try {
                const res = await labelService.GET_Label_ALL();
                setLabelOptions(flattenLabels(res?.data?.labels));
            } catch (e) {
                console.error(e);
                setLabelOptions([]);
            }
        })();
    }, [show]);

    // Statuses of every property picked in a "Set custom property" row
    const requestedProperties = useRef(new Set());
    useEffect(() => {
        const missing = [...new Set(extraActions.map((r) => r.propertyId).filter(Boolean))].filter(
            (id) => !fieldsByProperty[id] && !requestedProperties.current.has(id),
        );
        if (!missing.length) return;
        missing.forEach(async (propId) => {
            requestedProperties.current.add(propId);
            try {
                const res = await services.customfield.GET_CUSTOM_FIELD_BY_TASK_ID(propId);
                const options = (res?.data?.data || []).map((item) => ({
                    label: fieldLabel(item),
                    value: item?.customField?.id ?? item?.id,
                }));
                setFieldsByProperty((prev) => ({ ...prev, [propId]: options }));
            } catch (e) {
                console.error(e);
                setFieldsByProperty((prev) => ({ ...prev, [propId]: [] }));
            }
        });
    }, [extraActions, fieldsByProperty]);

    const updateExtra = (index, patch) =>
        setExtraActions((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

    // Action types a row may pick: not one another row already has, nor one that contradicts it
    const actionOptionsFor = (index) => {
        const others = extraActions.filter((_, i) => i !== index).map((r) => r.action).filter(Boolean);
        return EXTRA_ACTION_OPTIONS.filter((o) => {
            if (o.value === 'setCustomProperty') return true; // one row per property
            const group = EXCLUSIVE_GROUPS.find((g) => g.includes(o.value)) || [o.value];
            return !group.some((v) => others.includes(v));
        });
    };

    // Properties of the board the trigger watches
    useEffect(() => {
        if (!boardId) {
            setPropertyOptions([]);
            return;
        }
        (async () => {
            try {
                const res = await services.property.GET_PROPERTY_BY_BOARD_ID(boardId);
                setPropertyOptions(
                    (res?.data?.data || []).map((p) => ({ label: p.property?.propertyName, value: p.id })),
                );
            } catch (e) {
                console.error(e);
                setPropertyOptions([]);
            }
        })();
    }, [boardId]);

    // Statuses (custom fields) of the chosen property
    useEffect(() => {
        if (!propertyId) {
            setFieldOptions([]);
            return;
        }
        (async () => {
            try {
                const res = await services.customfield.GET_CUSTOM_FIELD_BY_TASK_ID(propertyId);
                setFieldOptions(
                    (res?.data?.data || []).map((item) => ({
                        label: fieldLabel(item),
                        value: item?.customField?.id ?? item?.id,
                    })),
                );
            } catch (e) {
                console.error(e);
                setFieldOptions([]);
            }
        })();
    }, [propertyId]);

    // Properties of the target board
    useEffect(() => {
        if (!targetBoardId) {
            setTargetPropertyOptions([]);
            return;
        }
        (async () => {
            try {
                const res = await services.property.GET_PROPERTY_BY_BOARD_ID(targetBoardId);
                setTargetPropertyOptions(
                    (res?.data?.data || []).map((p) => ({ label: p.property?.propertyName, value: p.id })),
                );
            } catch (e) {
                console.error(e);
                setTargetPropertyOptions([]);
            }
        })();
    }, [targetBoardId]);

    // Statuses of the chosen target property
    useEffect(() => {
        if (!targetPropertyId) {
            setTargetFieldOptions([]);
            return;
        }
        (async () => {
            try {
                const res = await services.customfield.GET_CUSTOM_FIELD_BY_TASK_ID(targetPropertyId);
                setTargetFieldOptions(
                    (res?.data?.data || []).map((item) => ({
                        label: fieldLabel(item),
                        value: item?.customField?.id ?? item?.id,
                    })),
                );
            } catch (e) {
                console.error(e);
                setTargetFieldOptions([]);
            }
        })();
    }, [targetPropertyId]);

    // Lists of the target board
    useEffect(() => {
        if (!targetBoardId) {
            setListOptions([]);
            return;
        }
        (async () => {
            try {
                const { data } = await services.board.SELECTED_BOARD(targetBoardId);
                setListOptions(
                    (data || [])
                        .slice()
                        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
                        .map((item) => ({ value: item.id, label: item.coloum })),
                );
            } catch (e) {
                console.error(e);
                setListOptions([]);
            }
        })();
    }, [targetBoardId]);

    // Target board with a ticket process: only the lists a ticket may be moved to (the status can be set in any list
    // of the IF board, so the ticket's own list is unknown)
    const [processAllowedIds, setProcessAllowedIds] = useState(null);
    useEffect(() => {
        setProcessAllowedIds(null);
        if (!targetBoardId) return;
        let cancelled = false;
        (async () => {
            try {
                const byBoard = await services.automations.LIST_PROCESS_ALLOWED({ fromBoardId: boardId });
                if (!cancelled) setProcessAllowedIds(byBoard?.[targetBoardId]?.allowedListIds || null);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { cancelled = true; };
    }, [boardId, targetBoardId]);
    const targetListOptions = processAllowedIds
        ? listOptions.filter((o) => targetListIds.includes(o.value) || processAllowedIds.includes(o.value))
        : listOptions;

    // Edit mode: load the saved rule
    useEffect(() => {
        if (!show) return;
        if (!editId) {
            setBoardId('');
            setPropertyId('');
            setFieldIds([]);
            setActionKind('trip');
            setTripStatus('');
            setTargetBoardId('');
            setTargetListIds([]);
            setTargetPropertyId('');
            setTargetFieldIds([]);
            setReverse(false);
            setExtraActions([]);
            setShowChecklist(false);
            setChecklistItems([]);
            setChecklistMandatory(false);
            return;
        }
        (async () => {
            try {
                const response = await services.automations.GET_BY_ID_AUTOMATIONS(editId);
                const rule = response?.data?.data;
                if (!rule) return;
                const when = rule.when || {};
                const action = (rule.action || [])[0] || {};
                setBoardId(when.boardId || '');
                setPropertyId(when.customPropertyId || '');
                setFieldIds(
                    Array.isArray(when.customFieldIds) && when.customFieldIds.length
                        ? when.customFieldIds
                        : when.customFieldId
                            ? [when.customFieldId]
                            : [],
                );
                setReverse(!!when.reverse);
                const actions = rule.action || [];
                const tripAct = actions.find((a) => a.action === 'setTripStatus');
                const moveAct = actions.find((a) => a.action === 'moveToList');
                // trip status (+ optional move) is the normal form; a move-only rule keeps the old list mode
                setActionKind(tripAct || !moveAct ? 'trip' : 'list');
                setTripStatus(tripAct?.tripStatus || '');
                setTargetBoardId(moveAct?.boardId || '');
                setTargetPropertyId(moveAct?.targetCustomPropertyId || '');
                setTargetFieldIds(asIds(moveAct?.targetCustomFieldIds));
                setExtraActions(actions.filter((a) => EXTRA_ACTION_VALUES.includes(a.action)).map(actionToRow));
                const savedChecklist = (Array.isArray(rule.checklist) ? rule.checklist : []).filter((i) => (i?.label || '').trim());
                setChecklistItems(
                    savedChecklist.map((i, ci) => ({
                        id: `c-${ci}`,
                        text: i.label,
                        ...orderFlagsFromSaved(i, orderModeOf(savedChecklist)),
                        checksBelow: !!i.checksBelow,
                        onCheck: i.onCheck,
                    })),
                );
                setShowChecklist(savedChecklist.length > 0);
                setChecklistMandatory(!!rule.checklistMandatory);
                setChecklistOrder(orderModeOf(savedChecklist));
                setTargetListIds(
                    moveAct
                        ? Array.isArray(moveAct.boardListIds) && moveAct.boardListIds.length
                            ? moveAct.boardListIds
                            : moveAct.boardListId
                                ? [moveAct.boardListId]
                                : []
                        : [],
                );
            } catch (e) {
                console.error(e);
            }
        })();
    }, [show, editId]);

    const label = (options, value) => options.find((o) => o.value === value)?.label || '';

    const handleSave = async () => {
        if (!boardId) return toast.error('Please select the board');
        if (!propertyId) return toast.error('Please select the property');
        if (!fieldIds.length) return toast.error('Please select the property status');
        if (actionKind === 'trip' && !tripStatus) return toast.error('Please select the trip status');
        // list is required for the move-only mode; in trip mode the move is optional but needs both board and list
        if ((actionKind === 'list' || targetBoardId || targetListIds.length) && (!targetBoardId || !targetListIds.length)) {
            return toast.error('Please select the board and list');
        }
        if (targetPropertyId && !targetFieldIds.length) return toast.error('Please select the status for the target property');
        // checklist (move mode only): "when checked" needs a list for a board, a status for a property
        const checklistToSave = actionKind === 'list' && showChecklist ? checklistItems.filter((i) => i.text.trim()) : [];
        const badItem = checklistToSave.find((i) => {
            const a = i.onCheck;
            if (!a) return false;
            const fieldCount = Array.isArray(a.customField) ? a.customField.length : 0;
            return (a.boardId && !a.listId) || (a.customProperty && !fieldCount);
        });
        if (badItem) return toast.error(`Task "${badItem.text.trim()}": when checked — select the list, and a status for the property`);
        const extras = actionKind === 'list' ? extraActions : [];
        for (const row of extras) {
            if (!row.action) return toast.error('Please select the action, or remove the empty row');
            if (row.action === 'assignTo' && !row.user) return toast.error('Please select the user to assign');
            if (row.action === 'assignToRoundRobin' && !(row.users || []).length) return toast.error('Please select the users for round robin');
            if (row.action === 'setEmailLabel' && !row.label?.name) return toast.error('Please select the label');
            if (row.action === 'setCustomProperty' && (!row.propertyId || !(row.fieldIds || []).length)) {
                return toast.error('Please select the property and its status');
            }
        }
        const extraPayload = extras.map((row, i) => {
            const base = { action: row.action, order: i + 1 };
            if (row.action === 'assignTo') return { ...base, entityId: row.user };
            if (row.action === 'assignToRoundRobin') {
                return { ...base, entityIds: row.users.map((u) => ({ entityId: u.value, name: u.label })) };
            }
            if (row.action === 'setEmailLabel') {
                return { ...base, labelName: row.label.name, ...(row.label.parentId ? { parentId: row.label.parentId } : {}) };
            }
            if (row.action === 'setCustomProperty') {
                return { ...base, customProperty: row.propertyId, customField: row.fieldIds.map((id) => ({ id })) };
            }
            return base;
        });

        const tripAction = { action: 'setTripStatus', order: 0, tripStatus };
        const moveAction = {
            action: 'moveToList',
            order: actionKind === 'trip' ? 1 : 0,
            boardId: targetBoardId,
            boardListId: targetListIds[0],
            boardListIds: targetListIds,
            ...(targetPropertyId && targetFieldIds.length
                ? { targetCustomPropertyId: targetPropertyId, targetCustomFieldIds: targetFieldIds }
                : {}),
        };
        const actions = actionKind === 'trip'
            ? (targetBoardId ? [tripAction, moveAction] : [tripAction])
            : [moveAction, ...extraPayload];

        const listNames = targetListIds.map((id) => label(listOptions, id)).join(', ');
        const target =
            actionKind === 'trip'
                ? `${label(TRIP_STATUS_OPTIONS, tripStatus)}${targetBoardId ? ` + ${listNames}` : ''}`
                : listNames;
        const statusNames = fieldIds.map((id) => label(fieldOptions, id)).join(', ');

        const payload = {
            type: PROPERTY_STATUS_LINK_TYPE,
            name: `${label(propertyOptions, propertyId)}: ${statusNames} -> ${target}`,
            // Several statuses only for the board/list action; the trip status action stays a single status
            when: {
                boardId,
                customPropertyId: propertyId,
                customFieldId: fieldIds[0],
                ...(actionKind === 'list' ? { customFieldIds: fieldIds } : {}),
                // the reverse option is hidden in move mode, so never save it on there
                reverse: actionKind === 'trip' ? reverse : false,
            },
            // added to the ticket when this automation moves it
            checklist: checklistToSave.map((i) => ({
                label: i.text.trim(),
                value: false,
                ...orderPayload(i, checklistOrder),
                ...(i.checksBelow ? { checksBelow: true } : {}),
                ...(hasChecklistItemAction(i.onCheck) ? { onCheck: i.onCheck } : {}),
            })),
            checklistMandatory: checklistToSave.length > 0 && checklistMandatory,
            condition: [{ operand: 'board', operator: 'equals', value: boardId }],
            action: actions,
        };

        setSaving(true);
        try {
            const response = !editId
                ? await services.automations.CREATE_AUTOMATIONS(payload)
                : await services.automations.UPDATE_AUTOMATIONS(editId, payload);
            if (response) {
                toast.success(editId ? 'Automations Updated Successfully' : 'Automations Added Successfully');
                onSaved && onSaved();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const footer = (
        <div style={{ display: 'flex', gap: '10px' }}>
            <button
                type="button"
                style={{
                    padding: '8px 20px', fontSize: '13px', fontWeight: '600', color: '#374151',
                    backgroundColor: '#f3f4f6', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer',
                }}
                onClick={onCancel || onHide}
            >
                Cancel
            </button>
            <button
                type="button"
                disabled={saving}
                style={{
                    padding: '8px 20px', fontSize: '13px', fontWeight: '600', color: '#fff',
                    backgroundColor: '#1a7d3e', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: saving ? 0.7 : 1,
                }}
                onClick={handleSave}
            >
                <FiSave size={13} />
                {editId ? 'Update' : 'Create'}
            </button>
        </div>
    );

    return (
        <AppModal
            show={show}
            onClose={onHide}
            icon={<FiZap size={17} color="#fff" />}
            title={editId ? 'Edit Automation' : 'Create a new automation'}
            subtitle="Link a property status to a trip status or a list"
            maxWidth="960px"
            footer={footer}
        >
            <div className="automation-form" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-700 w-[60px]">When</span>
                    <AppSelect
                        portal
                        value={PROPERTY_STATUS_LINK_TYPE}
                        style={{ ...selectStyle, color: '#1f2937', minWidth: '260px' }}
                        onChange={(e) => onTypeChange(e.target.value)}
                        options={WHEN_OPTIONS}
                    />
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <span style={labelStyle}>IF</span>
                    <AppSelect
                        portal
                        value={boardId}
                        onChange={(e) => {
                            setBoardId(e.target.value);
                            setPropertyId('');
                            setFieldIds([]);
                        }}
                        placeholder="Select board"
                        style={selectStyle}
                        options={boardName || []}
                    />
                    <AppSelect
                        portal
                        value={propertyId}
                        onChange={(e) => {
                            setPropertyId(e.target.value);
                            setFieldIds([]);
                        }}
                        placeholder={boardId ? 'Select property' : 'Select a board first'}
                        disabled={!boardId}
                        style={selectStyle}
                        options={propertyOptions}
                    />
                    {actionKind === 'list' ? (
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={fieldOptions}
                            value={fieldOptions.filter((o) => fieldIds.includes(o.value))}
                            onChange={(selected) => setFieldIds((selected || []).map((o) => o.value))}
                            placeholder={propertyId ? 'Select property statuses' : 'Select a property first'}
                            isDisabled={!propertyId}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={multiSelectStyles}
                        />
                    ) : (
                        <AppSelect
                            portal
                            value={fieldIds[0] || ''}
                            onChange={(e) => setFieldIds(e.target.value ? [e.target.value] : [])}
                            placeholder={propertyId ? 'Select property status' : 'Select a property first'}
                            disabled={!propertyId}
                            style={selectStyle}
                            options={fieldOptions}
                        />
                    )}
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <span style={labelStyle}>THEN</span>
                    <AppSelect
                        portal
                        value={actionKind}
                        onChange={(e) => {
                            const kind = e.target.value;
                            setActionKind(kind);
                            // the two options are separate: trip status only, or move only
                            setTargetBoardId('');
                            setTargetListIds([]);
                            setTargetPropertyId('');
                            setTargetFieldIds([]);
                            setExtraActions([]);
                            if (kind === 'trip') setFieldIds((prev) => prev.slice(0, 1));
                            else { setTripStatus(''); setReverse(false); }
                        }}
                        style={{ ...selectStyle, minWidth: '260px' }}
                        options={ACTION_KIND_OPTIONS}
                    />
                    {actionKind === 'trip' && (
                        <AppSelect
                            portal
                            value={tripStatus}
                            onChange={(e) => setTripStatus(e.target.value)}
                            placeholder="Select trip status"
                            style={selectStyle}
                            options={TRIP_STATUS_OPTIONS}
                        />
                    )}
                </div>

                {/* Move mode: pick the board and list. Trip mode shows it only for rules saved earlier with trip status + move. */}
                {(actionKind === 'list' || targetBoardId) && (
                <div className="flex items-center gap-3 flex-wrap">
                    <span style={labelStyle}>{actionKind === 'trip' ? 'AND' : ''}</span>
                    <span className="text-sm text-gray-700" style={{ minWidth: '200px' }}>
                        {actionKind === 'trip' ? 'move the ticket to' : 'to this board and list'}
                    </span>
                    <AppSelect
                        portal
                        value={targetBoardId}
                        onChange={(e) => {
                            setTargetBoardId(e.target.value);
                            setTargetListIds([]);
                            setTargetPropertyId('');
                            setTargetFieldIds([]);
                            // the actions' users and properties belong to the board: pick them again
                            setExtraActions([]);
                        }}
                        placeholder={actionKind === 'list' ? 'Select the new board' : 'Select board'}
                        style={selectStyle}
                        options={boardName || []}
                    />
                    {actionKind === 'list' ? (
                        // move mode: one board, one list
                        <AppSelect
                            portal
                            value={targetListIds[0] || ''}
                            onChange={(e) => setTargetListIds(e.target.value ? [e.target.value] : [])}
                            placeholder={targetBoardId ? 'Select new list' : 'Select the new board first'}
                            disabled={!targetBoardId}
                            style={selectStyle}
                            options={targetListOptions}
                        />
                    ) : (
                    <Select
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        options={targetListOptions}
                        value={targetListIds.map((id) => listOptions.find((o) => o.value === id)).filter(Boolean)}
                        onChange={(selected) => setTargetListIds((selected || []).map((o) => o.value))}
                        placeholder={targetBoardId ? 'Select lists' : 'Select a board first'}
                        isDisabled={!targetBoardId}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={multiSelectStyles}
                    />
                    )}
                    {actionKind === 'trip' && targetBoardId && (
                        <button
                            type="button"
                            onClick={() => { setTargetBoardId(''); setTargetListIds([]); setTargetPropertyId(''); setTargetFieldIds([]); }}
                            style={{ fontSize: '12px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                            Remove
                        </button>
                    )}
                </div>
                )}

                {/* Board and list only: this extra row is kept just for rules saved earlier with a target status */}
                {targetBoardId && targetPropertyId && (
                    <div className="flex items-center gap-3 flex-wrap">
                        <span style={labelStyle} />
                        <span className="text-sm text-gray-700" style={{ minWidth: '200px' }}>
                            and set this status there (optional)
                        </span>
                        <AppSelect
                            portal
                            value={targetPropertyId}
                            onChange={(e) => {
                                setTargetPropertyId(e.target.value);
                                setTargetFieldIds([]);
                            }}
                            placeholder="Select property"
                            style={selectStyle}
                            options={targetPropertyOptions}
                        />
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={targetFieldOptions}
                            value={targetFieldOptions.filter((o) => targetFieldIds.includes(o.value))}
                            onChange={(selected) => setTargetFieldIds((selected || []).map((o) => o.value))}
                            placeholder={targetPropertyId ? 'Select statuses' : 'Select a property first'}
                            isDisabled={!targetPropertyId}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={multiSelectStyles}
                        />
                    </div>
                )}

                {actionKind === 'list' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {extraActions.map((row, i) => {
                            const labelValue = row.label
                                ? labelOptions.find((o) => {
                                    const v = JSON.parse(o.value);
                                    return v.name === row.label.name && (v.parentId || null) === (row.label.parentId || null);
                                })?.value || ''
                                : '';
                            const fieldOptionsOfRow = fieldsByProperty[row.propertyId] || [];
                            return (
                                <div
                                    key={i}
                                    className="flex items-center gap-3 flex-wrap"
                                    style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px' }}
                                >
                                    <span style={labelStyle}>AND</span>
                                    <AppSelect
                                        portal
                                        value={row.action || ''}
                                        onChange={(e) => updateExtra(i, { action: e.target.value, user: '', users: [], label: null, propertyId: '', fieldIds: [] })}
                                        placeholder="Select Action"
                                        style={selectStyle}
                                        options={actionOptionsFor(i)}
                                    />
                                    {row.action === 'assignTo' && (
                                        <AppSelect
                                            portal
                                            value={row.user || ''}
                                            onChange={(e) => updateExtra(i, { user: e.target.value })}
                                            placeholder="Select user"
                                            style={{ ...selectStyle, minWidth: '240px' }}
                                            options={targetUserOptions}
                                        />
                                    )}
                                    {row.action === 'assignToRoundRobin' && (
                                        <Select
                                            isMulti
                                            closeMenuOnSelect={false}
                                            hideSelectedOptions={false}
                                            options={targetUserOptions}
                                            value={row.users || []}
                                            onChange={(selected) => updateExtra(i, { users: selected || [] })}
                                            placeholder="Select users"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={multiSelectStyles}
                                        />
                                    )}
                                    {row.action === 'setEmailLabel' && (
                                        <AppSelect
                                            portal
                                            value={labelValue}
                                            onChange={(e) => {
                                                if (!e.target.value) return updateExtra(i, { label: null });
                                                const v = JSON.parse(e.target.value);
                                                updateExtra(i, { label: { name: v.name, parentId: v.parentId || null } });
                                            }}
                                            placeholder="Select label"
                                            style={{ ...selectStyle, minWidth: '240px' }}
                                            options={labelOptions}
                                        />
                                    )}
                                    {row.action === 'setCustomProperty' && (
                                        <>
                                            <AppSelect
                                                portal
                                                value={row.propertyId || ''}
                                                onChange={(e) => updateExtra(i, { propertyId: e.target.value, fieldIds: [] })}
                                                placeholder="Select property"
                                                style={selectStyle}
                                                options={targetPropertyOptions.filter(
                                                    (o) => o.value === row.propertyId ||
                                                        !extraActions.some((r, j) => j !== i && r.action === 'setCustomProperty' && r.propertyId === o.value),
                                                )}
                                            />
                                            <Select
                                                isMulti
                                                closeMenuOnSelect={false}
                                                hideSelectedOptions={false}
                                                options={fieldOptionsOfRow}
                                                value={fieldOptionsOfRow.filter((o) => (row.fieldIds || []).includes(o.value))}
                                                onChange={(selected) => updateExtra(i, { fieldIds: (selected || []).map((o) => o.value) })}
                                                placeholder={row.propertyId ? 'Select statuses' : 'Select a property first'}
                                                isDisabled={!row.propertyId}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={multiSelectStyles}
                                            />
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        title="Remove action"
                                        onClick={() => setExtraActions((prev) => prev.filter((_, j) => j !== i))}
                                        style={{
                                            width: '28px', height: '28px', color: '#ef4444', background: '#fef2f2',
                                            border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            );
                        })}
                        <div>
                            <button
                                type="button"
                                className="automation-add-btn"
                                disabled={!targetBoardId}
                                title={targetBoardId ? '' : 'Select the new board first'}
                                style={{ opacity: targetBoardId ? 1 : 0.5, cursor: targetBoardId ? 'pointer' : 'not-allowed' }}
                                onClick={() => setExtraActions((prev) => [...prev, { action: '' }])}
                            >
                                + Add action
                            </button>
                        </div>
                        {extraActions.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                The actions run after the ticket is moved, on the new board. Labels only apply to email tickets.
                            </div>
                        )}
                    </div>
                )}

                {/* Checklist added to the ticket when it is moved (same options as the "item added" automation) */}
                {actionKind === 'list' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label className="flex items-center gap-2 text-sm text-gray-700" style={{ cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={showChecklist}
                                onChange={(e) => {
                                    setShowChecklist(e.target.checked);
                                    if (e.target.checked && !checklistItems.length) setChecklistItems([newChecklistItem()]);
                                }}
                                style={{ accentColor: '#1a7d3e', width: '15px', height: '15px' }}
                            />
                            Do you want to add a checklist?
                        </label>
                        {showChecklist && (
                            <>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#1a7d3e' }}>TASK CHECKLIST</div>
                                <OrderRuleRadio value={checklistOrder} onChange={setChecklistOrder} name="property-link-checklist-order" />
                                {checklistItems.map((item, idx) => (
                                    <div key={item.id} className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                                        <OrderBadge
                                            index={idx}
                                            mode={checklistOrder}
                                            optional={item.optional}
                                            onToggle={() => updateChecklistItem(item.id, { optional: !item.optional })}
                                        />
                                        <input
                                            type="text"
                                            className="border border-gray-300 rounded px-3 py-2 bg-white text-sm"
                                            style={{ flex: 1, minWidth: '200px' }}
                                            placeholder="Type task description here"
                                            value={item.text}
                                            onChange={(e) => updateChecklistItem(item.id, { text: e.target.value })}
                                        />
                                        {idx < checklistItems.length - 1 && (
                                            <label
                                                className="flex items-center gap-1 text-xs"
                                                title="When this task is checked on the ticket, every task below it is checked too"
                                                style={{ whiteSpace: 'nowrap', cursor: 'pointer', color: item.checksBelow ? '#1a7d3e' : '#6b7280' }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!item.checksBelow}
                                                    onChange={() => updateChecklistItem(item.id, { checksBelow: !item.checksBelow })}
                                                />
                                                Checks all below
                                            </label>
                                        )}
                                        <ChecklistItemAction
                                            value={item.onCheck}
                                            onChange={(onCheck) => updateChecklistItem(item.id, { onCheck })}
                                            boardOptions={boardName}
                                            ruleBoardId={targetBoardId}
                                            ruleListId={targetListIds[0]}
                                        />
                                        {checklistItems.length > 1 && (
                                            <button
                                                type="button"
                                                className="text-red-500 font-bold px-2"
                                                onClick={() => setChecklistItems((prev) => prev.filter((i) => i.id !== item.id))}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <div>
                                    <button
                                        type="button"
                                        className="automation-add-btn"
                                        onClick={() => setChecklistItems((prev) => [...prev, newChecklistItem()])}
                                    >
                                        + Add task
                                    </button>
                                </div>
                                <label className="flex items-center gap-2 text-sm text-gray-700" style={{ cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={checklistMandatory}
                                        onChange={(e) => setChecklistMandatory(e.target.checked)}
                                        style={{ accentColor: '#1a7d3e', width: '15px', height: '15px' }}
                                    />
                                    Is this checklist mandatory? (the ticket can only be moved to another list once every item is checked)
                                </label>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                    The checklist is added to the ticket when this automation moves it.
                                </div>
                            </>
                        )}
                    </div>
                )}

                {actionKind === 'list' && (fieldIds.length > 1 || targetListIds.length > 1) && (
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {fieldIds.length > 1 && 'The rule runs when the ticket gets any one of the selected statuses. '}
                        {targetListIds.length > 1 &&
                            'The ticket is moved to the first selected list; with reverse on, moving it into any selected list gives it all the selected statuses.'}
                    </div>
                )}

                {/* Move mode is just "status set -> ticket moved": no reverse option */}
                {actionKind === 'trip' && (
                <label className="flex items-center gap-2 text-sm text-gray-700" style={{ cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={reverse}
                        onChange={(e) => setReverse(e.target.checked)}
                        style={{ accentColor: '#1a7d3e', width: '15px', height: '15px' }}
                    />
                    {'Also apply in reverse: when the trip gets this status, add the property status to the ticket; when the property status is removed from the ticket, put the trip back to its previous status' +
                        (targetBoardId ? '. Moving the ticket into the selected list also adds the property status; leaving it removes it again' : '')}
                </label>
                )}
            </div>
        </AppModal>
    );
};

export default PropertyStatusLinkForm;
